import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import type { ReactNode } from 'react';

import { Logger } from '../services';

// Define the shape of the context state
interface ExtState {
  isExtEnabled: boolean;
  userQuery: string;
  isMenuPinned: boolean;
  isJobTriggerSuccess: boolean;
}

// Define a generic action that automatically infers from ExtState
interface Action<K extends keyof ExtState> {
  type: K;
  value: ExtState[K];
}

// Initial state
export const initialState: ExtState = {
  isExtEnabled: false,
  userQuery: '',
  isMenuPinned: false,
  isJobTriggerSuccess: false,
};

// Reducer function that infers the key and value types
const reducer = <K extends keyof ExtState>(
  state: ExtState,
  action: Action<K>
): ExtState => ({ ...state, [action.type]: action.value });

// Create the context with a default value
const ExtContext = createContext<
  | {
      extState: ExtState;
      updateExtState: <K extends keyof ExtState>(
        key: K,
        value: ExtState[K]
      ) => void;
    }
  | undefined
>(undefined);

// Provider component that wraps the children
export function ExtProvider({ children }: { children: ReactNode }) {
  const [extState, dispatch] = useReducer(reducer, initialState);

  // Function to dispatch actions based on key and value, with no hardcoding
  const updateExtState = useCallback(
    <K extends keyof ExtState>(key: K, value: ExtState[K]) => {
      dispatch({ type: key, value }); // No hardcoded strings!
    },
    []
  );

  // Memoize the context value to avoid unnecessary re-renders
  const value = useMemo(
    () => ({
      extState,
      updateExtState,
    }),
    [extState, updateExtState]
  );

  return <ExtContext.Provider value={value}>{children}</ExtContext.Provider>;
}

// Custom hook to use the Ext context
export const useExtContext = () => {
  const context = useContext(ExtContext);

  if (!context) {
    Logger.warn(
      'useExtContext not within an ExtProvider, returning default values.'
    );

    return {
      extState: initialState,
      updateExtState: () => {},
    };
  }

  return context;
};
